import { invisiblyApi } from 'src/redux/api/invisibly-api';
import { APITagType } from 'src/redux/api/invisibly-api.types';
import { createTagInvalidationArray } from 'src/redux/api/invisibly-api.helpers';
import { InvisiblyApiResponse } from 'src/types/apiResponses';
import {
  PulseCreateRequest,
  PulseDetail,
  PulseEnabledSources,
  PulseListResponse,
  PulseRunSummary,
  PulseSourceCatalogueItem,
} from './pulse.types';

const pulseEndpoints = invisiblyApi.injectEndpoints({
  endpoints: (builder) => ({
    getPulses: builder.query<PulseListResponse, void>({
      query: () => ({
        method: 'GET',
        url: 'pulses',
      }),
      transformResponse(response: InvisiblyApiResponse<PulseListResponse>) {
        return response.item;
      },
      providesTags: () => [{ id: 'LIST', type: APITagType.Pulse }],
    }),
    getPulse: builder.query<PulseDetail, { id: string }>({
      query: ({ id }) => ({
        method: 'GET',
        url: `pulses/${id}`,
      }),
      transformResponse(response: InvisiblyApiResponse<PulseDetail>) {
        return response.item;
      },
      providesTags: (_result, _error, arg) => [{ id: arg.id, type: APITagType.Pulse }],
    }),
    createPulse: builder.mutation<PulseDetail, PulseCreateRequest>({
      query: (body) => ({
        method: 'POST',
        url: 'pulses',
        data: body,
      }),
      transformResponse(response: InvisiblyApiResponse<PulseDetail>) {
        return response.item;
      },
      invalidatesTags: createTagInvalidationArray(APITagType.Pulse),
    }),
    pausePulse: builder.mutation<PulseDetail, { id: string }>({
      query: ({ id }) => ({
        method: 'POST',
        url: `pulses/${id}/pause`,
      }),
      transformResponse(response: InvisiblyApiResponse<PulseDetail>) {
        return response.item;
      },
      invalidatesTags: createTagInvalidationArray(APITagType.Pulse),
    }),
    resumePulse: builder.mutation<PulseDetail, { id: string }>({
      query: ({ id }) => ({
        method: 'POST',
        url: `pulses/${id}/resume`,
      }),
      transformResponse(response: InvisiblyApiResponse<PulseDetail>) {
        return response.item;
      },
      invalidatesTags: createTagInvalidationArray(APITagType.Pulse),
    }),
    runPulseNow: builder.mutation<
      { run: PulseRunSummary | null; pulse: PulseDetail },
      { id: string }
    >({
      query: ({ id }) => ({
        method: 'POST',
        url: `pulses/${id}/run`,
      }),
      transformResponse(
        response: InvisiblyApiResponse<{ run: PulseRunSummary | null; pulse: PulseDetail }>,
      ) {
        return response.item;
      },
      invalidatesTags: createTagInvalidationArray(APITagType.Pulse),
    }),
    backfillPulse: builder.mutation<
      { runs: PulseRunSummary[]; pulse: PulseDetail },
      { id: string; months?: number; window_start?: string; window_end?: string }
    >({
      query: ({ id, ...body }) => ({
        method: 'POST',
        url: `pulses/${id}/backfill`,
        data: body,
      }),
      transformResponse(
        response: InvisiblyApiResponse<{ runs: PulseRunSummary[]; pulse: PulseDetail }>,
      ) {
        return response.item;
      },
      invalidatesTags: createTagInvalidationArray(APITagType.Pulse),
    }),
    updatePulseSources: builder.mutation<
      PulseDetail,
      { id: string; enabled_sources: PulseEnabledSources }
    >({
      query: ({ id, enabled_sources }) => ({
        method: 'PATCH',
        url: `pulses/${id}/sources`,
        data: { enabled_sources },
      }),
      transformResponse(response: InvisiblyApiResponse<PulseDetail>) {
        return response.item;
      },
      invalidatesTags: createTagInvalidationArray(APITagType.Pulse),
    }),
    getPulseSources: builder.query<PulseSourceCatalogueItem[], void>({
      query: () => ({
        method: 'GET',
        url: 'pulse-sources',
      }),
      transformResponse(response: InvisiblyApiResponse<PulseSourceCatalogueItem[]>) {
        return response.item;
      },
    }),
  }),
  overrideExisting: true,
});

export const {
  useGetPulsesQuery,
  useGetPulseQuery,
  useCreatePulseMutation,
  usePausePulseMutation,
  useResumePulseMutation,
  useRunPulseNowMutation,
  useBackfillPulseMutation,
  useUpdatePulseSourcesMutation,
  useGetPulseSourcesQuery,
} = pulseEndpoints;
